import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { asMoney, uniqueId } from "../../../utils";

import AreaBookingItems from "./AreaBookingItems";
import { differenceInDays, parse } from "date-fns";
import { RegionTypeSelect } from "../../../components/MaterialSelects/RegionTypeSelect";
import { PosterSizeSelect } from "../../../components/MaterialSelects/PosterSizeSelect";
import { CurrencyField } from "../../../components/CurrencyInput";
import { IsoDatePicker } from "../../../components/IsoDatePicker";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";
import { AreaBooking } from "../../../generated/graphql";
import { CalculateAreaBookingPricing, useAreaPricingContext, useDaybookContext } from "../../../contexts/Daybook/DayBookContext";
import { ExchangeRatesContextValue, useExchangeRatesContext } from "../../../contexts/exchange-rates-context";
import { useCampaignContext } from "../../../contexts/campaign-context";



type ABPRowProps = {
    numPosters: number,
    firstUp: string,
    lastDown: string,
    numWeeks: number,
    calcValue: number,
    totalValue: number
};

function findPricing(pricing, id) {
    let found = false;
    let val = 0;
    let currency = null;
    if (!pricing) return 0;
    pricing.forEach((pricing) => {
        if (pricing.regionType.id === id) {
            val = pricing.unit_value_local;
            currency = pricing.currency;
            found = true;
        }
    });
    if(!found) return null;
    return {value:val,currency:currency};
}

// called whenever a contributor to the prescribed price changes
export function calculateSummaries({ data, calculateAreaBookingPricing,exchangeRatesContext, values }:{data:AreaBooking,calculateAreaBookingPricing:CalculateAreaBookingPricing<any>,exchangeRatesContext:ExchangeRatesContextValue, values:any}) {
    console.log('[CALCSUM]', data);
    let qty = 0;
    let firstDate:string|undefined|null = '';
    let lastDate:string|undefined|null = '';
    let numWeeks = 0;
    let totals = 0;
    const hasSingleCurrency = true;
    //var currency = null;
    if (data.areaBookingItems) {
        data.areaBookingItems.forEach((v) => {
            if(!v) return;
            let localQty = 0;
            if (data?.regionType?.has_split_qty) {
                console.log('[CALCSUM]','splitqty',(v.qty_sites && v.qty_posters_per_site) ? v.qty_posters_per_site * v.qty_sites : 0);
                localQty = (v.qty_sites && v.qty_posters_per_site) ? v.qty_posters_per_site * v.qty_sites : 0;
            } else {
                console.log('[CALCSUM]','not-split',v.campaignComponent?.qty??0);
                localQty = v.campaignComponent?.qty??0;
            }
            firstDate = firstDate ? ((v.date_up??'') < firstDate ? v.date_up : firstDate) : v.date_up??'';
            lastDate = lastDate ? ((v.date_down??'') > lastDate ? v.date_down : lastDate) : v.date_down??'';
            if (v.date_up && v.date_down) {
                // duration (for non-regional pricing) is the longest duration of any child item
                const d = Math.abs(differenceInDays(parse(v.date_up, 'yyyy-MM-dd', new Date()), parse(v.date_down, 'yyyy-MM-dd', new Date())));
                numWeeks = Math.ceil(d / 7) > numWeeks ? Math.ceil(d / 7) : numWeeks;
                console.log('[CALCSUM]','dur',numWeeks,calculateAreaBookingPricing);
            }
            if (data?.regionType?.has_regional_pricing && calculateAreaBookingPricing) {
                totals += exchangeRatesContext!.calcExchangeRate(v.campaignComponent?.currency,calculateAreaBookingPricing(v)*localQty); // this already does local qty
                console.log('[CALCSUM]','HRP',v,localQty,totals);
            }
            qty += localQty;
        });
    } else {
        numWeeks = 0;
    }
    let unitValue = 0, totalValue = 0, calcValue = 0;
    if (!data?.regionType?.has_regional_pricing) {
        unitValue = data.unit_value_prescribed_local??0;
        if (numWeeks === 1) {
            if(values?.salesTeam?.prefix == "UUK" || values?.salesTeam?.prefix == "ULC") {
                if(data.posterSize?.name == 'UA Paste Up' || data.posterSize?.name == 'UA Megasite' ) {
                    calcValue = Math.floor(unitValue * 0.5);
                }
                else {
                    calcValue = Math.floor(unitValue * 1);
                }
            }
            else {
                calcValue = Math.floor(unitValue * 0.75); 
            }
        } else {
            calcValue = Math.floor(unitValue * numWeeks / 2);
        }
        totalValue = exchangeRatesContext?.calcExchangeRate(data.currency,calcValue,qty);
        console.log('[CALCSUM]','non-regionalpricing',{uv:unitValue,cv:calcValue,tv:totalValue,exchangeRatesContext});
    } else {
        unitValue = 0;
        calcValue = totals;
        totalValue = totals;
    }
    console.log('[CALCSUM]','return', {
        numPosters: qty,
        firstUp: firstDate,
        lastDown: lastDate,
        numWeeks: numWeeks,
        calcValue: calcValue,
        totalValue: totalValue,
        hasSingleCurrency:hasSingleCurrency
    });
    return {
        numPosters: qty,
        firstUp: firstDate,
        lastDown: lastDate,
        numWeeks: numWeeks,
        calcValue: calcValue,
        totalValue: totalValue,
        hasSingleCurrency:hasSingleCurrency
    }
}

function makeColumns(config, isReadOnly,exchangeRatesContext, values) {
    const columns: FieldTableColumnProps<AreaBooking,ABPRowProps>[] = [
        {
            header: '',
            dataType: 'expander',
            accessor: 'status',
        }, {
            header: 'Region Type',
            accessor: 'regionType',
            dataType: 'select',
            sort: 'regionType.name',
            readOnly: ({ row }) => (isReadOnly(true) || !!row.areaBookingItems?.length),
            options: {
                label: 'name',
                // eslint-disable-next-line react/display-name, react/prop-types
                renderEditor: ({ field, row, rowName }) => {
                    return (<RegionTypeSelect {...field} autoFocus={true} retainItemProps={true}
                        disabled={!!row.areaBookingItems?.length}
                        onChange={
                            (evt) => {
                                if(!field?.setValue) return;
                                const newVal = evt.target.value;
                                if (!config.regionAreaBookingPricing) {
                                    const posterSize = row.posterSize;
                                    const newId = newVal.id;
                                    if (posterSize && posterSize.id && row.regionType && row.regionType.has_poster_pricing) {
                                        const val = findPricing(posterSize.regionTypePricing, newId);
                                        if (val) {
                                            field.setValue(rowName + '.unit_value_prescribed_local', val.value);
                                            field.setValue(rowName+'.currency',val.currency);
                                        } else {
                                            field.setValue(rowName + '.unit_value_prescribed_local',0);
                                            field.setValue(rowName+'.currency',null);
                                        }
                                    }
                                }
                                field.setValue(rowName + '.regionType', newVal);
                            }
                        }
                    />);
                }
            }
        }, {
            header: 'Poster Size',
            accessor: 'posterSize',
            sort: 'posterSize',
            dataType: 'select',
            readOnly: () => { return isReadOnly(true); },
            options: {
                label: 'name',
                // eslint-disable-next-line react/display-name, react/prop-types
                renderEditor: ({ field, row, rowName}) => {
                    return <PosterSizeSelect values={values} {...field} inTable={true} autoFocus={true} isStreetPoster={true}
                        retainItemProps={true}
                        onChange={
                            (evt) => {
                                const newVal = evt.target.value;
                                const regionType = row['regionType'];
                                if (regionType && regionType.has_poster_pricing) {
                                    if (regionType.has_regional_pricing) {
                                    
                                        // find regional pricing
                                        const val = findPricing(newVal?.regionTypePricing, regionType.id);
                                        if (val) {
                                            field.setValue(rowName + '.unit_value_prescribed_local', val.value);
                                            field.setValue(rowName + '.currency', val.currency);
                                        } else {
                                            field.setValue(rowName + '.unit_value_prescribed_local', 0);
                                            field.setValue(rowName + '.currency', null);
                                        }
                                    } else {
                                        // use "{nameOfRegionType}Pricing" field from graphql
                                        if (newVal[regionType?.name?.toLowerCase() + 'Pricing']) {
                                        
                                            field.setValue(rowName + '.unit_value_prescribed_local', newVal[regionType?.name?.toLowerCase() + 'Pricing']);
                                            const cId = newVal[regionType?.name?.toLowerCase() + 'CurrencyId'];
                                            const currency = exchangeRatesContext.systemOptions.find((option)=>option.id==cId);
                                            field.setValue(rowName + '.currency', currency);
                                            console.log('LOK SET',cId,newVal,rowName + '.currency', currency);
                                        } else {
                                            field.setValue(rowName + '.unit_value_prescribed_local',0);
                                            field.setValue(rowName + '.currency', null);

                                        }
                                    }
                                }
                                field.onChange(evt);
                            }
                        }

                    />;
                }
            }
        }, {
            header: 'Total Posters',
            className: 'column-int',
            accessor: 'numPosters',
            dataType: 'number',
            readOnly: true,
            options: {
                getValue: ({ rowProps }) => {
                    return rowProps?.numPosters;
                }
            }
        }];
    //        const columns2: FieldTableColumnProps<AreaBooking>[] = config.regionAreaBookingPricing ? []
    const columns2: FieldTableColumnProps<AreaBooking,ABPRowProps>[] = [{
        /* this is the value from the database for 2 weeks */
        header: 'Unit Value',
        accessor: 'unit_value_prescribed_local',
        //currencyAccessor: 'currency',
        dataType: 'currency',
        nullRenderAs:'',
        readOnly: ({ row }) => {
            const rt = row?.regionType;
            return !rt || !rt.id || rt.has_poster_pricing || rt.has_regional_pricing;
        },
        // eslint-disable-next-line react/display-name, react/prop-types
        options: {
            currency: {
                accessor:'currency'
            },
            getValue:({row,field})=>{
                if (row?.regionType?.has_regional_pricing) return null;
                return field.value;
            },
            // eslint-disable-next-line react/display-name, react/prop-types
            renderEditor: ({ field, row }) => {
                const regionType = row.regionType;
                const posterSizeId = row.posterSize;
                if (regionType && regionType.id && (!regionType.has_poster_pricing) && posterSizeId) {
                    return <CurrencyField currencySelect={true} currency_value={field.currency} {...field}
                        autoFocus={true} />;
                }
                return (<span className="uneditable">{field.currency?.glyph}{asMoney(field.value)}</span>);
            }
        }
    }, {
        /* this is the calculated Unit Price */
        header: 'Calc Value',
        accessor: 'calcValue',
        dataType: 'currency',
        readOnly: true,
        nullRenderAs:'',
        // eslint-disable-next-line react/display-name, react/prop-types
        options: {
            currency: {
                accessor:'currency'
            },
            getValue: ({ rowProps }) => rowProps?.calcValue
        }
    }, {
        header: 'Weeks',
        dataType: 'number',
        accessor: 'numWeeks',
        options: {
            getValue: ({ rowProps }) => rowProps?.numWeeks
        },
        readOnly: true
    }, {
        header: 'Total Value',
        accessor: 'totalValue',
        dataType: 'currency',
        readOnly: true,
        nullRenderAs:'',
        options: {
            getValue: ({ rowProps }) => {
                //if (row?.regionType?.has_regional_pricing) { return null; }
                if(!rowProps) return null;
                return rowProps?.totalValue;
            }
        }
    },
    /* {
        header: 'Total Quote Value',
        accessor: 'totalValueQuote',
        className: 'column-currency',
        dataType: 'currency',
        readOnly: true,
        hideWhen: () => false,
        // eslint-disable-next-line react/display-name, react/prop-types
        options: {
        }
    }*/
    ];

    const columns3: FieldTableColumnProps<AreaBooking,ABPRowProps>[] = [{
        header: 'Received',
        className: 'column-date',
        accessor: 'posters_received',
        dataType: 'date',
        options: {
            renderEditor: ({ field }) => (<IsoDatePicker {...field} />)
        }
    }, {
        header: 'First Up',
        accessor: 'firstUp',
        dataType: 'date',
        readOnly: true,
        options: {
            getValue: ({ rowProps }) => (rowProps?.firstUp)
        }
    }, {
        header: 'Last Down',
        accessor: 'lastDown',
        dataType: 'date',
        readOnly: true,
        options: {
            getValue: ({ rowProps }) => (rowProps?.lastDown)
        }
    }, {
        header: 'Bay Number',
        accessor: 'bay_number',
        dataType: 'number'
    }, {
        header: 'Star Rating',
        accessor: 'rating',
        dataType: 'number'
    }];

    const column4 = config?.useAreaBookingToggle
    ? [
        {
            header: 'Pro Photos',
            accessor: 'is_photo_paid_ab',
            dataType: 'booleanCamToggle'
        }
    ]
    : [];

    return columns.concat(...columns2, ...columns3, ...column4);
}



function newRow():AreaBooking {
    return ({
        id:uniqueId(),
        posterSize: null,
        areaBookingItems: [],
        currency: null,
        regionType: null,
        unit_value_local: null,
        unit_value_prescribed_local: null,
        posters_received: null,
        bay_number: null,
        rating: null
    });
}


const AreaBookingPane = () => {
    const {
        values,
        setValue,
        onChange,
        errors,
        loading,
        readOnly,
        setTab,
        isReadOnly
    } = useOutletContext<CampaignOutletContextValue>();
    useEffect(() => {
        setTab('area-bookings');
    }, []);
    const config = useDaybookContext();
    const calculateAreaBookingPricing = useCampaignContext().calculateAreaPricing;
    const exchangeRatesContext = useExchangeRatesContext();
    const [columns,setColumns] = useState<FieldTableColumnProps<AreaBooking,ABPRowProps>[]>(makeColumns(config, isReadOnly,exchangeRatesContext,values));

    useEffect(()=>{
        setColumns(makeColumns(config, isReadOnly,exchangeRatesContext,values));
    },[config,isReadOnly,exchangeRatesContext,values]);

    const allowAddDelete = !(readOnly || isReadOnly(true));
    return (
        <FieldTable columns={columns}
            name="areaBookings"
            className="campaigns__area-bookings"
            getRowProps={(data)=>{
                return calculateSummaries({...data,calculateAreaBookingPricing,exchangeRatesContext, values});
            }}
            newRow={newRow}
            errors={errors}
            hasDelete={allowAddDelete}
            values={values?.areaBookings??[]}
            setValue={setValue}
            loading={loading}
            readOnly={readOnly}
            newRowLabel={allowAddDelete ? 'Add Area Booking' : undefined}>{
                ({ name, row }) => <AreaBookingItems row={row}
                    splitAmounts={!!row?.regionType?.has_split_qty}
                    hasRegionalPricing={!!row?.regionType?.has_regional_pricing}
                    readOnly={readOnly} loading={loading} errors={errors}
                    name={name} values={row} setValue={setValue}
                    onChange={onChange} isReadOnly={isReadOnly} />
            }
        </FieldTable>
    );
}
AreaBookingPane.propTypes = {
    setValue: PropTypes.func,
    values: PropTypes.array,
    onChange: PropTypes.func
};

export default AreaBookingPane;
